import 'core-js/modules/es.promise.js';
import _someInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/some';
import * as React from 'react';
import classnames from 'classnames';
import styled from 'styled-components';
import { selectFromTheme } from '@thisisbud/sc-utils';
import { paragraphClassName, inputLabelClassName, paragraphCss, h4ClassName } from '@thisisbud/bui-react-typography';
import { dividerCss } from '@thisisbud/bui-react-divider';
import { Checkbox } from '@thisisbud/bui-react-checkbox';
import { RightChevronIcon } from '@thisisbud/bui-react-icons';
import { jsxs, jsx } from 'react/jsx-runtime';
import { TextButton } from '@thisisbud/bui-react-buttons';
import 'core-js/modules/web.dom-collections.iterator.js';
import _forEachInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/for-each';
import _everyInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/every';
import _mapInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/map';
import { useFormikContext } from 'formik';

const entityListClassName = 'bui-entity-list';
const entityListFieldsetClassName = `${entityListClassName}__fieldset`;
const entityListTitleClassName = `${entityListClassName}__title`;
const entityListHeadingClassName = `${entityListClassName}__heading`;
const entityListActionClassName = `${entityListClassName}__action`;
const entityListListClassName = `${entityListClassName}__list`;

var _span, _div;
const entityListItemClassName = 'bui-entity-list-item';
const entityListItemWrapperClassName = `${entityListItemClassName}-wrapper`;
const entityListItemSelectionClassName = `${entityListItemClassName}-selection`;
const entityListItemContentClassName = `${entityListItemClassName}-content`;
const entityListItemChevronClassName = `${entityListItemClassName}-chevron`;
const entityListItemDecorationClassName = `${entityListItemClassName}-decoration`;

/**
 * Get the tag name to render on the list item wrapper.
 *
 * @param type - The type of list item
 * @returns The tag name to render on the list item wrapper
 */
function getWrapperComponent(type) {
  switch (type) {
    case 'checkbox':
      return 'label';
    case 'anchor':
      return 'a';
    case 'button':
      return 'button';
    default:
      return 'div';
  }
}

/**
 * Render a checkbox to select the list item.
 *
 * @param props - The component props
 */
function renderCheckbox(props) {
  const {
    disabled,
    name,
    onBlur,
    onChange
  } = props;
  return /*#__PURE__*/jsx("span", {
    className: entityListItemSelectionClassName,
    children: /*#__PURE__*/jsx(Checkbox, {
      disabled: disabled,
      name: name,
      onBlur: onBlur,
      onChange: onChange
    })
  });
}
function renderChevron() {
  return _span || (_span = /*#__PURE__*/jsx("span", {
    className: entityListItemChevronClassName,
    children: /*#__PURE__*/jsx(RightChevronIcon, {})
  }));
}

/**
 * A list item component to display an entity as part of a list.
 * The `EntityListItem` component is meant to be used together with the `EntityList` component.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { EntityList, EntityListItem } from '@thisisbud/bui-react-entity-list'
 *
 * function Component() {
 *   return (
 *     <EntityList title='Example list'>
 *       <EntityListItem>Item 1</EntityListItem>
 *       <EntityListItem>Item 2</EntityListItem>
 *     </EntityList>
 *   )
 * }
 * ```
 */
function EntityListItem(props) {
  const {
    children,
    className,
    id,
    // @ts-expect-error Prop only available on the `checkbox` and 'button' types
    disabled,
    // @ts-expect-error Prop only available on the `checkbox` and 'button' types
    name,
    // @ts-expect-error Prop only available on the `anchor` and 'button' types
    onClick,
    type,
    // @ts-expect-error Prop only available on the 'button' type
    value
  } = props;
  const {
    href,
    rel,
    target
  } = props;
  const WrapperComponent = getWrapperComponent(type); // eslint-disable-line @typescript-eslint/naming-convention

  return /*#__PURE__*/jsxs("li", {
    className: classnames(entityListItemClassName, className),
    children: [/*#__PURE__*/jsxs(WrapperComponent, {
      className: entityListItemWrapperClassName,
      disabled: disabled,
      href: href,
      id: id,
      name: name,
      rel: rel,
      target: target,
      value: value,
      onClick: onClick,
      children: [type === 'checkbox' ? renderCheckbox(props) : null, /*#__PURE__*/jsx("span", {
        className: entityListItemContentClassName,
        children: children
      }), type === 'anchor' || type === 'button' ? renderChevron() : null]
    }), typeof type !== 'undefined' && type !== 'default' && disabled !== true ? _div || (_div = /*#__PURE__*/jsx("div", {
      className: entityListItemDecorationClassName
    })) : null]
  });
}
EntityListItem.displayName = 'EntityListItem';

/**
 * Render the action for the standard list.
 *
 * @param props - The component props
 */
function renderAction(props) {
  // @ts-expect-error Props will not always be available
  const {
    actionLabel,
    actionValue,
    actionVariant,
    onActionClick
  } = props;
  const hasActionOption = typeof actionLabel !== 'undefined' && typeof onActionClick === 'function';
  if (!hasActionOption) {
    return null;
  }
  return /*#__PURE__*/jsx("div", {
    className: entityListActionClassName,
    children: /*#__PURE__*/jsx(TextButton, {
      value: actionValue,
      variant: actionVariant,
      onClick: onActionClick,
      children: actionLabel
    })
  });
}

/**
 * Render the title for the standard list.
 *
 * @param props - The component props
 */
function renderTitle(title) {
  if (typeof title === 'undefined') {
    return null;
  }
  return /*#__PURE__*/jsx("span", {
    className: entityListTitleClassName,
    children: title
  });
}

/**
 * Render the heading component for the standard list.
 *
 * @param props - The component props
 * @param titleId - The ID of the title
 */
function renderHeadingComponent(props, titleId) {
  // @ts-expect-error Props will not always be available
  const {
    title,
    actionLabel,
    titleHeadingLevel = 4
  } = props;
  if (typeof title === 'undefined' && typeof actionLabel === 'undefined') {
    return null;
  }
  const HeadingComponent = typeof title !== 'undefined' ? `h${titleHeadingLevel}` : 'div'; // eslint-disable-line @typescript-eslint/naming-convention

  return /*#__PURE__*/jsxs(HeadingComponent, {
    className: entityListHeadingClassName,
    id: titleId,
    children: [renderTitle(title), renderAction(props)]
  });
}

/**
 * A list component to display entities of any kind.
 * The `EntityList` component is meant to be used together with the `EntityListItem` component.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { EntityList, EntityListItem } from '@thisisbud/bui-react-entity-list'
 *
 * function Component() {
 *   return (
 *     <EntityList title='Example list'>
 *       <EntityListItem>Item 1</EntityListItem>
 *       <EntityListItem>Item 2</EntityListItem>
 *     </EntityList>
 *   )
 * }
 * ```
 */
function StandardEntityList(props) {
  const {
    children,
    id,
    ordered,
    // @ts-expect-error Props will not always be available
    title
  } = props;
  const ListComponent = ordered === true ? 'ol' : 'ul'; // eslint-disable-line @typescript-eslint/naming-convention
  let titleId;
  if (typeof title === 'string' && typeof id === 'string') {
    titleId = `${id}-title`;
  }
  return /*#__PURE__*/jsxs(React.Fragment, {
    children: [renderHeadingComponent(props, titleId), /*#__PURE__*/jsx(ListComponent, {
      "aria-labelledby": titleId,
      className: entityListListClassName,
      children: children
    })]
  });
}
StandardEntityList.displayName = 'StandardEntityList';

/**
 * Get the names of the list item selection inputs.
 *
 * @param children - The entity list item nodes
 * @returns The names of the item selection inputs
 */
function getSelectInputNames(children) {
  var _context;
  const output = [];
  _forEachInstanceProperty(_context = React.Children).call(_context, children, function (child) {
    const {
      name
    } = child.props;
    if (typeof name === 'string') {
      output.push(name);
    }
  });
  return output;
}

/**
 * Use a handler function for click events on the Select All button.
 *
 * @param children - The children of the list component
 * @param onSelectAllClick - A function to call when the event is triggered
 * @returns A function to handle click events
 */
function useOnSelectAllClickHandler(children, onSelectAllClick) {
  const {
    getFieldHelpers
  } = useFormikContext();
  return React.useCallback(function (e) {
    var _context2;
    if (typeof onSelectAllClick === 'function') {
      onSelectAllClick(e);
    }
    if (e.defaultPrevented) {
      return;
    }

    // Set all list items to selected
    _forEachInstanceProperty(_context2 = getSelectInputNames(children)).call(_context2, function (inputName) {
      const {
        setValue
      } = getFieldHelpers(inputName);
      setValue(true);
    });
  }, [children, getFieldHelpers, onSelectAllClick]);
}

/**
 * Use a handler function for click events on the Deselect All button.
 *
 * @param children - The children of the list component
 * @param onDeselectAllClick - A function to call when the event is triggered
 * @returns A function to handle click events
 */
function useOnDeselectAllClickHandler(children, onDeselectAllClick) {
  const {
    getFieldHelpers
  } = useFormikContext();
  return React.useCallback(function (e) {
    var _context3;
    if (typeof onDeselectAllClick === 'function') {
      onDeselectAllClick(e);
    }
    if (e.defaultPrevented) {
      return;
    }

    // Set all list items to selected
    _forEachInstanceProperty(_context3 = getSelectInputNames(children)).call(_context3, function (inputName) {
      const {
        setValue
      } = getFieldHelpers(inputName);
      setValue(false);
    });
  }, [children, getFieldHelpers, onDeselectAllClick]);
}

/**
 * A list component to display selectable entities of any kind.
 * The `EntityList` component is meant to be used together with the `EntityListItem` component.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { EntityList, EntityListItem } from '@thisisbud/bui-react-entity-list'
 *
 * function Component() {
 *   return (
 *     <EntityList id='example' selectAllLabel='Select all' title='Example list'>
 *       <EntityListItem name='example1' type='checkbox'>Item 1</EntityListItem>
 *       <EntityListItem name='example2' type='checkbox'>Item 2</EntityListItem>
 *     </EntityList>
 *   )
 * }
 * ```
 */
function SelectableItemsEntityList(props) {
  var _context4, _context5;
  const {
    children,
    deselectAllLabel,
    disabled,
    id,
    onDeselectAllClick,
    onSelectAllClick,
    ordered,
    selectAllLabel,
    title
  } = props;
  const {
    values
  } = useFormikContext();
  const ListComponent = ordered === true ? 'ol' : 'ul'; // eslint-disable-line @typescript-eslint/naming-convention
  const titleId = `${id}-title`;
  const showDeselectAll = _everyInstanceProperty(_context4 = getSelectInputNames(children)).call(_context4, function (inputName) {
    return values[inputName];
  }) && typeof deselectAllLabel !== 'undefined';
  const onDeselectAllClickHandler = useOnDeselectAllClickHandler(children, onDeselectAllClick);
  const onSelectAllClickHandler = useOnSelectAllClickHandler(children, onSelectAllClick);
  return /*#__PURE__*/jsxs("fieldset", {
    "aria-labelledby": titleId,
    className: entityListFieldsetClassName,
    children: [/*#__PURE__*/jsxs("div", {
      className: entityListHeadingClassName,
      children: [/*#__PURE__*/jsx("legend", {
        className: entityListTitleClassName,
        id: titleId,
        children: title
      }), typeof selectAllLabel !== 'undefined' ? /*#__PURE__*/jsx("div", {
        className: entityListActionClassName,
        children: /*#__PURE__*/jsx(TextButton, {
          disabled: disabled,
          onClick: showDeselectAll ? onDeselectAllClickHandler : onSelectAllClickHandler,
          children: showDeselectAll ? deselectAllLabel : selectAllLabel
        })
      }) : null]
    }), /*#__PURE__*/jsx(ListComponent, {
      className: entityListListClassName,
      children: _mapInstanceProperty(_context5 = React.Children).call(_context5, children, function (child) {
        return /*#__PURE__*/React.cloneElement(child, {
          disabled
        });
      })
    })]
  });
}
SelectableItemsEntityList.displayName = 'SelectableItemsEntityList';

let _$1 = t => t,
  _t$1;
// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledEntityList = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledEntityList",
  componentId: "sc-1gd99dy-0"
})(_t$1 || (_t$1 = _$1`
  .${0} {
    margin: 0;
  }

  .${0} {
    line-height: 0;
  }
`), entityListTitleClassName, entityListActionClassName);

/**
 * Render the title component for the selectable list.
 *
 * @param props - The component props
 */
function renderTitleComponent(props, titleId) {
  const {
    onChange,
    name,
    id,
    title,
    titleHeadingLevel = 4
  } = props;
  const TitleComponent = `h${titleHeadingLevel}`; // eslint-disable-line @typescript-eslint/naming-convention

  const selectId = `${id}-checkbox-input`;
  return /*#__PURE__*/jsxs("div", {
    className: entityListHeadingClassName,
    children: [/*#__PURE__*/jsx(TitleComponent, {
      className: entityListTitleClassName,
      children: /*#__PURE__*/jsx("label", {
        htmlFor: selectId,
        id: titleId,
        children: title
      })
    }), /*#__PURE__*/jsx("div", {
      className: entityListActionClassName,
      children: /*#__PURE__*/jsx(Checkbox, {
        id: selectId,
        name: name,
        onChange: onChange
      })
    })]
  });
}

/**
 * A list component to display entities of any kind and select them all together.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { EntityList } from '@thisisbud/bui-react-entity-list'
 * import { Form } from '@thisisbud/bui-react-form'
 *
 *
 * function Component() {
 *   return (
 *   <Form initialValues={{}}>
 *      <EntityList title='Example list'>
 *        <EntityListItem>Item 1</EntityListItem>
 *        <EntityListItem>Item 2</EntityListItem>
 *      </EntityList>
 *   </Form>
 *   )
 * }
 * ```
 */

function SelectableEntityList(props) {
  const {
    children,
    id,
    ordered
  } = props;
  const ListComponent = ordered === true ? 'ol' : 'ul'; // eslint-disable-line @typescript-eslint/naming-convention

  const titleId = `${id}-title`;
  return /*#__PURE__*/jsxs(StyledEntityList, {
    children: [renderTitleComponent(props, titleId), /*#__PURE__*/jsx(ListComponent, {
      "aria-labelledby": titleId,
      className: entityListListClassName,
      children: children
    })]
  });
}
SelectableEntityList.displayName = 'SelectableEntityList';

let _ = t => t,
  _t;
// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledWrapper",
  componentId: "sc-olvu9q-0"
})(_t || (_t = _`
  display: block;
  padding: 0;
  margin: 0;
  border: 0;
  box-sizing: border-box;

  & + & {
    margin-top: ${0};
  }

  .${0} + & {
    margin-top: ${0};
  }

  .${0} + & {
    margin-top: ${0};
  }

  fieldset {
    display: block;
    padding: 0;
    margin: 0;
    border: 0;
  }

  .${0} {
    ${0}
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: ${0} 0;
    font-weight: ${0};

    strong {
      font-weight: ${0};
    }
  }

  .${0} legend {
    display: block;
    padding: 0;
  }

  .${0} {
    margin-left: auto;
  }

  .${0} + .${0} {
    margin-left: ${0};
  }

  .${0} {
    padding: 0;
    margin: 0;
    list-style: none;

    > li {
      position: relative;
      margin: 0;
    }
  
    > li::marker {
      display: none;
    }
  
    > li::before {
      ${0}
      content: "";
      position: absolute;
      width: 100%;
      top: 0;
      left: 0;
      z-index: 0;
    }
  }

  .${0} + .${0} {
    margin-top: ${0};
  }

  .${0} + & {
    margin-top: ${0};
  }

  .${0} {
    position: absolute;
    width: 100%;
    height: calc(100% - (2 * ${0}));
    padding: ${0};
    left: 50%;
    top: 50%;
    box-sizing: content-box;
    background-color: ${0};
    border-color: transparent;
    border-radius: ${0};
    border-style: solid;
    border-width: ${0};
    z-index: 0;
    transform: translate(-50%, -50%);
    transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
  }

  .${0} {
    position: relative;
    display: flex;
    width: 100%;
    align-items: center;
    padding: ${0} 0;
    margin: 0;
    color: inherit;
    text-align: left;
    text-decoration: inherit;
    font-size: ${0};
    background-color: transparent;
    border: 0;
    outline: 0;
    appearance: none;
    z-index: 1;

    :hover + .${0} {
      background-color: ${0};
    }

    :focus + .${0} {
      border-color: ${0};
    }

    :active + .${0} {
      background-color: ${0};
    }
  }

  button.${0} {
    cursor: pointer;

    :disabled {
      cursor: default;
      opacity: ${0};
    }
  }

  .${0} {
    display: block;
    padding-right: ${0};
  }

  .${0} {
    display: block;
    flex-basis: 100%;
    max-width: 100%;
  }

  .${0} {
    display: block;
    padding-left: ${0};
    font-size: ${0};
  }
`), selectFromTheme('spacingXSmallDefault'), paragraphClassName, selectFromTheme('spacingLargeDefault'), inputLabelClassName, selectFromTheme('spacingXXXSmallDefault'), entityListHeadingClassName, paragraphCss, selectFromTheme('spacingXXSmallDefault'), selectFromTheme('fontWeightHeading3'), selectFromTheme('fontWeightHeading3'), entityListHeadingClassName, entityListActionClassName, entityListTitleClassName, entityListActionClassName, selectFromTheme('spacingXSmallDefault'), entityListListClassName, dividerCss, entityListHeadingClassName, entityListListClassName, selectFromTheme('spacingXXXSmallDefault'), h4ClassName, selectFromTheme('spacingXSmallDefault'), entityListItemDecorationClassName, selectFromTheme('outlineWidthInteractiveListItemDefault'), selectFromTheme('paddingInteractiveListItemDefault'), selectFromTheme('colorBackgroundInteractiveListItemDefault'), selectFromTheme('borderRadiusInteractiveListItemDefault'), selectFromTheme('outlineWidthInteractiveListItemDefault'), entityListItemWrapperClassName, selectFromTheme('spacingXXSmallDefault'), selectFromTheme('fontSizeBodyRegularDefault'), entityListItemDecorationClassName, selectFromTheme('colorBackgroundInteractiveListItemHover'), entityListItemDecorationClassName, selectFromTheme('colorTextPrimaryAccent'), entityListItemDecorationClassName, selectFromTheme('colorBackgroundInteractiveListItemActive'), entityListItemWrapperClassName, selectFromTheme('opacityInteractiveListItemDisabled'), entityListItemSelectionClassName, selectFromTheme('spacingXXSmallDefault'), entityListItemContentClassName, entityListItemChevronClassName, selectFromTheme('spacingXXSmallDefault'), selectFromTheme('fontSizeBodyRegularDefault'));

/**
 * Checks if the entity list contains any selectable items.
 *
 * @param props - The component props
 * @returns An indication of presence of selectable list items
 */
function getIsSelectableByItems(props) {
  var _context;
  const {
    children
  } = props;
  return _someInstanceProperty(_context = React.Children.toArray(children)).call(_context, function (child) {
    const {
      type
    } = child.props;
    return type === 'checkbox';
  });
}

/**
 * Checks if the entity list is a selectable by list component.
 *
 * @param props - The component props
 * @returns A boolean indicating whether the component is a selectable by list entity
 */
function getIsSelectableByList(props) {
  // @ts-expect-error Props will not always be available
  const {
    name
  } = props;

  // TODO: break the list down into the individual types as exports. This would make it better for tree shaking.
  return typeof name === 'string';
}

/**
 * Render the right type of entity list based on the props passed.
 *
 * @param props - The component props
 * @returns The rendered entity list type
 */
function renderEntityListByType(props) {
  const isSelectableByItems = getIsSelectableByItems(props);
  const isSelectableByList = getIsSelectableByList(props);
  if (isSelectableByItems) {
    return /*#__PURE__*/React.createElement(SelectableItemsEntityList, props);
  } else if (isSelectableByList) {
    return /*#__PURE__*/React.createElement(SelectableEntityList, props);
  }
  return /*#__PURE__*/React.createElement(StandardEntityList, props);
}

/**
 * A list component to display entities of any kind.
 * The `EntityList` component is meant to be used together with the `EntityListItem` component.
 *
 * *When using the list with selectable items, it becomes necessary to use a title and id for accessibility purposes. It also becomes necessary to make the component a descendent of a Form component.*
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { EntityList, EntityListItem } from '@thisisbud/bui-react-entity-list'
 *
 * function Component() {
 *   return (
 *     <EntityList title='Example list'>
 *       <EntityListItem>Item 1</EntityListItem>
 *       <EntityListItem>Item 2</EntityListItem>
 *     </EntityList>
 *   )
 * }
 * ```
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { EntityList, EntityListItem } from '@thisisbud/bui-react-entity-list'
 *
 * function Component() {
 *   return (
 *     <EntityList id='example' selectAllLabel='Select all' title='Example list'>
 *       <EntityListItem name='example1' type='checkbox'>Item 1</EntityListItem>
 *       <EntityListItem name='example2' type='checkbox'>Item 2</EntityListItem>
 *     </EntityList>
 *   )
 * }
 * ```
 */
function EntityList(props) {
  const {
    className,
    id
  } = props;
  return /*#__PURE__*/jsx(StyledWrapper, {
    className: classnames(entityListClassName, className),
    id: id,
    children: renderEntityListByType(props)
  });
}
EntityList.displayName = 'EntityList';

export { EntityList, EntityListItem, entityListClassName, entityListItemClassName };
